<template>
  <div class="invoice-detail-view">
    <v-skeleton-loader
      v-if="isLoading"
      type="table"
      class="invoice-detail-loader"
    />

    <div v-else class="pa-4">
      <div class="summary-row mb-4">
        <div class="summary-box">
          <div class="summary-label">Invoices</div>
          <div class="summary-value">{{ InvoiceDetail.length }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Total Sales</div>
          <div class="summary-value">{{ totalSale | currency }}</div>
        </div>

        <div v-if="!hideCost" class="summary-box">
          <div class="summary-label">Total Cost</div>
          <div class="summary-value">{{ totalCost | currency }}</div>
        </div>

        <div v-if="!hideProfit" class="summary-box">
          <div class="summary-label">Total Profit</div>
          <div class="summary-value">{{ totalProfit | currency }}</div>
        </div>
      </div>

      <v-data-table
        dense
        readonly
        :sort-by="sortBy"
        :mobile-breakpoint="0"
        :headers="headers"
        :items-per-page="InvoiceDetail.length"
        hide-default-footer
        :items="InvoiceDetail"
        class="invoice-detail-table"
      >
        <template v-slot:item="{ item }">
          <tr class="invoice-detail-row">
            <td class="text-left">
              <a href="#" @click.prevent="viewInvoice(item.InvoiceId)">
                {{ item.Invoice ? item.Invoice.localId : item.InvoiceId }}
              </a>
            </td>

            <td class="text-left">
              <div v-if="item.Invoice && item.Invoice.Customer">
                {{ item.Invoice.Customer.name }}
              </div>
              <div v-else>Unknown Customer</div>

              <div
                v-if="item.Invoice && item.Invoice.Customer"
                class="customer-contact mt-1"
              >
                <v-chip
                  v-if="item.Invoice.Customer.phoneNumber"
                  x-small
                  class="mr-1"
                >
                  <v-icon x-small left>mdi-phone</v-icon>
                  {{ item.Invoice.Customer.phoneNumber }}
                </v-chip>

                <v-chip
                  v-if="item.Invoice.Customer.cellNumber"
                  x-small
                  color="yellow lighten-4"
                  text-color="yellow darken-4"
                >
                  <v-icon x-small left>mdi-cellphone-basic</v-icon>
                  {{ item.Invoice.Customer.cellNumber }}
                </v-chip>
              </div>
            </td>

            <td class="text-left">
              <div>
                {{ item.Invoice ? item.Invoice.date : null | moment("MM/DD/YYYY") }}
              </div>

              <div v-if="item.imeiNumber" class="imei-text">
                {{ item.imeiNumber }}
              </div>
            </td>

            <td class="text-center font-weight-bold">
              {{ item.qty }}
            </td>

            <td class="text-right font-weight-bold">
              {{ item.totalSale | currency }}
            </td>

            <td v-if="!hideCost" class="text-right">
              {{ item.totalCost | currency }}
            </td>

            <td
              v-if="!hideProfit"
              class="text-right font-weight-bold"
              :style="{ color: color(item) }"
            >
              {{ (item.totalSale - item.totalCost) | currency }}
            </td>

            <td class="text-left">
              <span v-if="item.Invoice && item.Invoice.User">
                {{ item.Invoice.User.name }}
              </span>
              <span v-else>N/A</span>
            </td>
          </tr>
        </template>

        <template v-slot:body.append v-if="InvoiceDetail.length > 0">
          <tr class="table-total-row">
            <td colspan="3" class="text-right font-weight-bold">
              Total
            </td>

            <td class="text-center font-weight-bold">
              {{ totalQty }}
            </td>

            <td class="text-right font-weight-bold">
              {{ totalSale | currency }}
            </td>

            <td v-if="!hideCost" class="text-right font-weight-bold">
              {{ totalCost | currency }}
            </td>

            <td v-if="!hideProfit" class="text-right font-weight-bold">
              {{ totalProfit | currency }}
            </td>

            <td></td>
          </tr>
        </template>
      </v-data-table>

      <div v-if="InvoiceDetail.length === 0" class="empty-state">
        No invoice details found for this product.
      </div>
    </div>
  </div>
</template>

<script>
import invoiceDetail from "@/modules/Invoice/service.js";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "invoice-detail-report",

  props: ["ProductId", "CustomerId", "hideProfit", "hideCost", "start", "end"],

  data() {
    return {
      isLoading: true,
      InvoiceDetail: [],
      sortBy: "InvoiceId",
      headers: [
        {
          text: "Invoice #",
          align: "left",
          value: "Invoice.localId",
          sortable: true,
        },
        {
          text: "Customer",
          align: "left",
          value: "Invoice.Customer.name",
          sortable: true,
        },
        {
          text: "Date",
          align: "left",
          value: "Invoice.date",
          sortable: true,
        },
        {
          text: "Qty",
          align: "center",
          value: "qty",
          sortable: true,
        },
        {
          text: "Price",
          align: "right",
          value: "totalSale",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    totalProfit() {
      return this.totalSale - this.totalCost;
    },

    totalSale() {
      return this.InvoiceDetail.reduce((sum, row) => {
        return sum + Number(row.totalSale || 0);
      }, 0);
    },

    totalPrice() {
      return this.totalSale;
    },

    totalCost() {
      return this.InvoiceDetail.reduce((sum, row) => {
        return sum + Number(row.totalCost || 0);
      }, 0);
    },

    totalQty() {
      return this.InvoiceDetail.reduce((sum, row) => {
        return sum + Number(row.qty || 0);
      }, 0);
    },
  },

  watch: {
    CustomerId() {
      this.getInvoiceDetail();
    },

    ProductId() {
      this.getInvoiceDetail();
    },

    start() {
      this.getInvoiceDetail();
    },

    end() {
      this.getInvoiceDetail();
    },
  },

  async created() {
    this.buildHeaders();
    await this.getInvoiceDetail();
  },

  methods: {
    buildHeaders() {
      if (!this.hideCost) {
        this.headers.push({
          text: "Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        });
      }

      if (!this.hideProfit) {
        this.headers.push({
          text: "Profit",
          align: "right",
          value: "totalProfit",
          sortable: true,
        });
      }

      this.headers.push({
        text: "User",
        align: "left",
        value: "Invoice.User.name",
        sortable: true,
      });
    },

    viewInvoice(invoice) {
      const hashId = hashids.encode(invoice);

      const routeData = this.$router.resolve({
        path: this.$route.meta.public
          ? `/public/invoice/${hashId}/view`
          : `/global/invoice/${hashId}/view`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    color(item) {
      const profit = Number(item.totalSale || 0) - Number(item.totalCost || 0);
      return profit < 0 ? "red" : "black";
    },

    async getInvoiceDetail() {
      this.isLoading = true;

      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;
      if (this.ProductId) where.ProductId = this.ProductId;

      try {
        const response = await invoiceDetail.getAllInvoiceDetail(where);
        this.$Progress.finish();
        this.InvoiceDetail = response.data || [];
      } catch (error) {
        console.error("getInvoiceDetail failed", error);
        this.InvoiceDetail = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-detail-view {
  background: #ffffff;
}

.invoice-detail-loader {
  border-radius: 16px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.summary-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
}

.summary-label {
  font-size: 12px;
  color: #64748b;
}

.summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
}

.invoice-detail-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.invoice-detail-row:hover {
  background: #f8fafc;
}

.customer-contact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.imei-text {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.table-total-row {
  background: #f8fafc;
}

.empty-state {
  text-align: center;
  color: #64748b;
  font-size: 13px;
  padding: 28px 0 8px;
}

@media (max-width: 960px) {
  .summary-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .summary-row {
    grid-template-columns: 1fr;
  }
}

@media print {
  .no-print {
    display: none;
  }

  * {
    background: transparent !important;
    color: #000 !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }
}
</style>